import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Página pública para que el cliente firme una reparación desde su propio dispositivo.
 * Sin autenticación. Acceso únicamente por token.
 */
export default function SignPublic() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/signature/${token}`);
        setData(r.data);
      } catch (e) {
        const d = e?.response?.data;
        const detail = d?.detail;
        if (typeof detail === "object" && detail !== null) {
          setError(detail.message || "Enlace no válido");
        } else {
          setError(detail || "Enlace no válido");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  useEffect(() => {
    if (loading || error || done || !data) return;
    const c = canvasRef.current;
    if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    c.width = c.clientWidth * ratio;
    c.height = c.clientHeight * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.clientWidth, c.clientHeight);
    ctx.strokeStyle = "#111110";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, [loading, error, done, data]);

  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0];
    return { x: (t ? t.clientX : e.clientX) - rect.left, y: (t ? t.clientY : e.clientY) - rect.top };
  };
  const start = (e) => {
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    drawingRef.current = true;
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasInk(true);
  };
  const stop = () => {
    drawingRef.current = false;
  };
  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.clientWidth, c.clientHeight);
    setHasInk(false);
  };
  const submit = async () => {
    if (!hasInk) {
      toast.error("Por favor, firma dentro del recuadro");
      return;
    }
    setSubmitting(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      await axios.post(`${API}/public/signature/${token}`, { image_b64: dataUrl });
      setDone(true);
    } catch (e) {
      const d = e?.response?.data;
      const detail = d?.detail;
      if (typeof detail === "object" && detail !== null) {
        toast.error(detail.message || "No se pudo enviar la firma");
      } else {
        toast.error(detail || "No se pudo enviar la firma");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F7F4] flex items-center justify-center">
        <div className="text-sm text-[#525252]">Cargando…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#F7F7F4] flex items-center justify-center p-4">
        <div className="ds-card p-8 max-w-md text-center">
          <div className="text-4xl mb-3">⚠</div>
          <h1 className="text-lg font-bold mb-2">Enlace no disponible</h1>
          <p className="text-sm text-[#525252]">{error}</p>
        </div>
      </div>
    );
  }
  if (done || data?.already_signed) {
    return (
      <div className="min-h-screen bg-[#F7F7F4] flex items-center justify-center p-4">
        <div className="ds-card p-8 max-w-md text-center">
          <div className="w-12 h-12 rounded-full bg-[#6E8B3D] text-white mx-auto flex items-center justify-center text-2xl mb-3">
            ✓
          </div>
          <h1 className="text-lg font-bold mb-1">¡Firma recibida!</h1>
          <p className="text-sm text-[#525252]">Gracias. Ya puedes cerrar esta ventana.</p>
        </div>
      </div>
    );
  }

  const r = data?.repair || {};
  const c = data?.company || {};
  return (
    <div className="min-h-screen bg-[#F7F7F4] py-6 px-4">
      <Toaster position="top-center" richColors />
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="ds-card p-5 mb-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
            {c.name || "Reparación"}
          </div>
          <h1 className="text-xl font-bold text-[#111110] mt-1">Firma de aceptación</h1>
          <p className="text-xs text-[#525252] mt-1">
            Por favor, firma dentro del recuadro para confirmar la entrega y la aceptación del
            trabajo realizado.
          </p>
        </div>
        {/* Datos */}
        <div className="ds-card p-5 mb-3 text-sm space-y-1">
          <Row k="Nº reparación" v={r.code} />
          <Row k="Cliente" v={r.customer_name} />
          <Row k="Equipo" v={`${r.device_type || ""} ${r.brand || ""} ${r.model || ""}`.trim() || "—"} />
          <Row k="Nº de serie" v={r.serial || "—"} />
          <Row k="Fecha de entrada" v={r.entry_date || "—"} />
          <Row k="Estado" v={(r.status || "").replace("_", " ")} />
          {r.issue && <Row k="Observaciones" v={r.issue} />}
        </div>
        {/* Canvas */}
        <div className="ds-card p-5">
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252] block mb-2">
            Firma aquí
          </label>
          <div className="border-2 border-dashed border-[#E5E5E0] bg-white">
            <canvas
              ref={canvasRef}
              className="w-full h-64 block touch-none"
              onMouseDown={start}
              onMouseMove={move}
              onMouseUp={stop}
              onMouseLeave={stop}
              onTouchStart={start}
              onTouchMove={move}
              onTouchEnd={stop}
              data-testid="public-signature-canvas"
            />
          </div>
          <div className="flex gap-2 justify-end mt-3">
            <button
              onClick={clear}
              disabled={submitting}
              className="px-4 py-2 border border-[#E5E5E0] text-sm hover:bg-[#F7F7F4]"
              data-testid="public-signature-clear"
            >
              Borrar
            </button>
            <button
              onClick={submit}
              disabled={submitting || !hasInk}
              className="px-5 py-2 bg-[#6E8B3D] text-white text-sm hover:bg-[#5d7634] disabled:opacity-50"
              data-testid="public-signature-submit"
            >
              {submitting ? "Enviando…" : "Confirmar firma"}
            </button>
          </div>
          <p className="text-[10px] text-[#525252] mt-3 leading-relaxed">
            Al firmar aceptas la entrega del equipo y las condiciones del servicio descritas. Se
            registrará la fecha, hora, IP y navegador usados al firmar.
          </p>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex gap-3 py-1">
      <div className="w-36 text-[#525252] font-mono text-[10px] uppercase tracking-wider pt-0.5">{k}</div>
      <div className="flex-1 text-[#111110]">{v || "—"}</div>
    </div>
  );
}
